/**
 * Panel del pedido.
 *
 * El borde dentado de abajo no es decoración gratuita: le dice al usuario que
 * eso que está viendo es la cuenta, la misma que va a salir impresa. Cuando
 * el mesero le muestra la pantalla al cliente, la metáfora se lee sola.
 */

import { cop, hhmm, pad2 } from '../lib/formato';
import { cambiarCantidad } from '../datos/repositorio';
import type { PedidoActual } from '../datos/vistas';

interface Props {
  pedido: PedidoActual;
  iconoDe: (productoId: string) => string;
  onAgregar: () => void;
  onEditarDatos: () => void;
  onPedirCuenta: () => void;
  /** Deshace una cuenta vacía abierta por error. */
  onDescartar: () => void;
  onCobrar: () => void;
  onPropina: (aceptada: boolean) => void;
}

export function PanelOrden({
  pedido,
  iconoDe,
  onAgregar,
  onEditarDatos,
  onPedirCuenta,
  onDescartar,
  onCobrar,
  onPropina,
}: Props) {
  const { orden, items, totales } = pedido;
  const hayOrden = orden !== null;

  return (
    <section className="card order">
      <div className="order-head">
        <div className="order-title">
          <h1>
            {orden
              ? (orden.etiqueta ?? `Mesa ${pad2(orden.mesaNumero ?? '—')}`)
              : 'Sin mesa seleccionada'}
          </h1>
          <button
            className="iconbtn"
            title="Editar comensales y mesero"
            onClick={onEditarDatos}
            disabled={!hayOrden}
          >
            ✎
          </button>
          <div style={{ flex: 1 }} />
          <button className="btn btn-gold" onClick={onAgregar} disabled={!hayOrden}>
            + Agregar productos
          </button>
        </div>

        {orden && (
          <div className="meta">
            <span>{orden.estado === 'cuenta_solicitada' ? 'Cuenta pedida' : 'Ocupada'}</span>
            {/* Con etiqueta, el número del puesto pasa a ser un dato más. */}
            {orden.etiqueta && (
              <span>
                Puesto <b className="mono">{pad2(orden.mesaNumero ?? '—')}</b>
              </span>
            )}
            <span>
              Abierta <b className="mono">{hhmm(orden.abiertaEn)}</b>
            </span>
            <span>
              Pedido <b className="mono">#{orden.numero}</b>
              {!orden.sincronizada && (
                <b style={{ color: 'var(--accent-600)' }} title="Aún no confirmada por el servidor">
                  {' '}
                  ·
                </b>
              )}
            </span>
            <span>
              Mesero <b>{orden.meseroNombre}</b>
            </span>
            <span>
              Comensales <b className="mono">{orden.comensales}</b>
            </span>
          </div>
        )}
      </div>

      <div className="items">
        {!hayOrden && (
          <div className="empty">
            <div className="big">▦</div>
            <p>
              Toca una mesa libre para abrirla, o una mesa ocupada para ver su pedido.
            </p>
          </div>
        )}

        {hayOrden && items.length === 0 && (
          <div className="empty">
            <div className="big">🍽️</div>
            <p>La mesa está vacía. Agrega el primer producto para comenzar el pedido.</p>
            <button className="btn btn-gold" onClick={onAgregar}>
              + Agregar productos
            </button>
          </div>
        )}

        {items.map((i) => (
          <div className="item" key={i.id}>
            <div className="thumb">{iconoDe(i.productoId)}</div>
            <div>
              <div className="name">{i.nombreSnap}</div>
              <div className="unit mono">{cop(i.precioUnitario)} c/u</div>
            </div>
            <div className="stepper">
              <button onClick={() => void cambiarCantidad(i.id, -1)} aria-label="Quitar uno">
                −
              </button>
              <span className="q mono">{i.cantidad}</span>
              <button onClick={() => void cambiarCantidad(i.id, 1)} aria-label="Agregar uno">
                +
              </button>
            </div>
            <div className="line-total mono">{cop(i.precioUnitario * i.cantidad)}</div>
          </div>
        ))}
      </div>

      <div className="order-foot">
        <div className="row">
          <span>Subtotal</span>
          <span className="mono">{cop(totales.subtotal)}</span>
        </div>
        <div className="row">
          <label className="tip">
            <input
              type="checkbox"
              checked={orden?.propinaAceptada ?? false}
              disabled={!hayOrden}
              onChange={(e) => onPropina(e.target.checked)}
            />
            <span className="switch" />
            <span>Propina voluntaria 10%</span>
          </label>
          <span className="mono">{cop(totales.propina)}</span>
        </div>
        <div className="row total">
          <span>Total a pagar</span>
          <span className="amount mono">{cop(totales.total)}</span>
        </div>

        <div className="actions">
          {/*
            Con la cuenta vacía, "Pedir cuenta" no tiene sentido —no hay nada
            que cobrar— así que ese espacio lo ocupa el descarte. Es justo el
            momento en que el mesero se da cuenta de que tocó la mesa
            equivocada.
          */}
          {hayOrden && items.length === 0 ? (
            <button className="btn btn-ghost" onClick={onDescartar}>
              Cancelar mesa
            </button>
          ) : (
            <button
              className="btn btn-ghost"
              onClick={onPedirCuenta}
              disabled={!hayOrden || orden?.estado === 'cuenta_solicitada'}
            >
              Pedir cuenta
            </button>
          )}
          <button className="btn btn-primary" onClick={onCobrar} disabled={items.length === 0}>
            Cerrar y cobrar
          </button>
        </div>
      </div>

      <div className="zig" />
    </section>
  );
}
